import { createStore } from 'zustand/vanilla';

import { Agreement, Gender, Handedness, Patient } from '@MP/shared/api/hooks/patient';
import BirthUtils from '@MP/shared/utils/BirthUtils';
import PhoneNumberUtils from '@MP/shared/utils/PhoneNumberUtils';
import ReplaceUtils from '@MP/shared/utils/ReplaceUtils';

const toString = (value: number) => {
  return value.toString().padStart(2, '0');
};

export interface PatientEditFormDataState {
  id: string;
  originCode: string;
  validCode: boolean | undefined;

  name: string;
  code: string;
  phoneNumber: string;
  birth: string;
  handedness: Handedness;
  gender?: Gender;
  // 발송할 동의서 선택 상태 (AgreementType 단위)
  sendPatientRegistration: boolean;
  sendStimHome: boolean;
  agreed: Agreement[];

  setAgreementsStatus: (agreements?: Agreement[]) => void;
  setExistsCode: (exists: boolean) => void;
  setCode: (code: string) => void;
  setName: (name: string) => void;
  setBirth: (birth: string) => void;
  setPhoneNumber: (phoneNumber: string) => void;
  setGender: (gender: Gender) => void;
  setHandedness: (handedness: Handedness) => void;
  validateBirth: () => boolean;
  setSendPatientRegistration: (checked: boolean) => void;
  setSendStimHome: (checked: boolean) => void;
  validatePhoneNumber: () => boolean;
}

export const createPatientEditFormDataStore = (user: Patient) =>
  createStore<PatientEditFormDataState>((set, get) => ({
    id: user.id,
    name: user.name,
    originCode: user.code,
    code: user.code,
    validCode: true,
    phoneNumber: user.phoneNumber || '',
    handedness: user.handedness || 'NONE',
    gender: user.gender,
    birth: user.birthYear ? `${user.birthYear}${toString(user.birthMonth || 0)}${toString(user.birthDay || 0)}` : '',
    sendPatientRegistration: false,
    sendStimHome: false,
    agreed: [],

    setAgreementsStatus: (agreements) => set({ agreed: agreements || [] }),

    setExistsCode: (exists) => set({ validCode: !exists }),

    setCode: (code) => {
      const trimmed = code.trim();
      set({ code: trimmed, validCode: trimmed !== get().originCode ? undefined : true });
    },
    setName: (name) => set({ name: name.trim() }),
    setBirth: (birth) => set({ birth: ReplaceUtils.replaceNumber(birth) }),
    setPhoneNumber: (phoneNumber) => set({ phoneNumber: ReplaceUtils.replaceNumber(phoneNumber) }),
    setGender: (gender) => set({ gender }),
    setHandedness: (handedness) => set({ handedness }),

    validateBirth: () => BirthUtils.isValid(get().birth),

    setSendPatientRegistration: (checked) => set({ sendPatientRegistration: checked }),
    setSendStimHome: (checked) => set({ sendStimHome: checked }),

    /**
     * phoneNumber를 입력하지 않은 경우는 true
     */
    validatePhoneNumber: () => {
      const { phoneNumber } = get();
      if (phoneNumber) {
        return PhoneNumberUtils.isValid(phoneNumber);
      }
      return true;
    },
  }));

export type PatientEditFormDataStoreApi = ReturnType<typeof createPatientEditFormDataStore>;

export const selectAgreedTypes = (s: PatientEditFormDataState) => s.agreed.map((v) => v.serviceType);

// STIM HOME 동의는 serviceType 이 아닌 AgreementType('STIM_HOME') 으로 식별
export const selectIsStimHomeAgreed = (s: PatientEditFormDataState): boolean =>
  s.agreed.some((v) => v.type === 'STIM_HOME');

// 회원가입(MINDD 서비스 이용) 동의는 AgreementType('PATIENT_REGISTRATION') 으로 식별
export const selectIsPatientRegistrationAgreed = (s: PatientEditFormDataState): boolean =>
  s.agreed.some((v) => v.type === 'PATIENT_REGISTRATION');

// 각 동의에서 만료된 항목이 하나라도 있으면 만료로 간주
export const selectIsPatientRegistrationExpired = (s: PatientEditFormDataState): boolean =>
  s.agreed.some((v) => v.type === 'PATIENT_REGISTRATION' && v.expired);

export const selectIsStimHomeExpired = (s: PatientEditFormDataState): boolean =>
  s.agreed.some((v) => v.type === 'STIM_HOME' && v.expired);

export const selectIsStimExpired = (s: PatientEditFormDataState): boolean =>
  s.agreed.some((v) => v.serviceType === 'STIM' && v.expired);

// 동의한 항목 중 만료된 게 하나라도 있는지
export const selectHasExpiredAgreement = (s: PatientEditFormDataState): boolean => s.agreed.some((v) => v.expired);

export const selectIsValidUser = (s: PatientEditFormDataState): boolean =>
  !!(s.code && s.name && s.validateBirth() && s.validatePhoneNumber());

export const selectIsValid = (s: PatientEditFormDataState): boolean =>
  !!(
    s.validCode &&
    s.code &&
    s.name &&
    s.validateBirth() &&
    s.validatePhoneNumber() &&
    (s.sendPatientRegistration || s.sendStimHome)
  );

/** PatientEditForm이 읽는 필드·액션 묶음(모두 원시값·안정 참조). useShallow와 함께 구독한다. */
export const selectEditFormFields = (s: PatientEditFormDataState) => ({
  originCode: s.originCode,
  name: s.name,
  code: s.code,
  phoneNumber: s.phoneNumber,
  birth: s.birth,
  handedness: s.handedness,
  gender: s.gender,
  setExistsCode: s.setExistsCode,
  setCode: s.setCode,
  setName: s.setName,
  setBirth: s.setBirth,
  setPhoneNumber: s.setPhoneNumber,
  setGender: s.setGender,
  setHandedness: s.setHandedness,
  validateBirth: s.validateBirth,
  validatePhoneNumber: s.validatePhoneNumber,
});
